import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  addDay,
  computeAvailability,
  isoToday,
  reservationFreeDate,
  type AvailabilityRow,
  type BookedRange,
  type CarAvailability,
} from "@/lib/availability";

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date");

/** Public: availability for every car (manual admin locks + live reservations). */
export const listCarAvailability = createServerFn({ method: "GET" }).handler(async () => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const today = isoToday();

  const [{ data: rows }, { data: bookings }] = await Promise.all([
    supabaseAdmin.from("car_availability").select("car_slug, fully_booked, blocked_until, note"),
    supabaseAdmin
      .from("bookings")
      .select("car_slug, pickup_date, return_date, stage, status")
      .gte("return_date", today),
  ]);

  const rowBySlug = new Map<string, AvailabilityRow>();
  for (const r of (rows ?? []) as AvailabilityRow[]) rowBySlug.set(r.car_slug, r);

  const rangesBySlug = new Map<string, BookedRange[]>();
  for (const b of bookings ?? []) {
    if (b.stage === "cancelled" || b.stage === "no_show" || b.status === "cancelled") continue;
    const list = rangesBySlug.get(b.car_slug) ?? [];
    list.push({ pickup_date: b.pickup_date, return_date: b.return_date });
    rangesBySlug.set(b.car_slug, list);
  }

  const slugs = new Set<string>([...rowBySlug.keys(), ...rangesBySlug.keys()]);
  const result: Record<string, CarAvailability> = {};
  for (const slug of slugs) {
    result[slug] = computeAvailability(
      slug,
      rowBySlug.get(slug) ?? null,
      rangesBySlug.get(slug) ?? [],
      today,
    );
  }
  return result;
});

/** Public: availability for a single car. */
export const getCarAvailability = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => z.object({ slug: z.string().min(1).max(160) }).parse(data))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const today = isoToday();

    const [{ data: row }, { data: bookings }] = await Promise.all([
      supabaseAdmin
        .from("car_availability")
        .select("car_slug, fully_booked, blocked_until, note")
        .eq("car_slug", data.slug)
        .maybeSingle(),
      supabaseAdmin
        .from("bookings")
        .select("pickup_date, return_date, stage, status")
        .eq("car_slug", data.slug)
        .gte("return_date", today),
    ]);

    const ranges: BookedRange[] = (bookings ?? [])
      .filter((b) => b.stage !== "cancelled" && b.stage !== "no_show" && b.status !== "cancelled")
      .map((b) => ({ pickup_date: b.pickup_date, return_date: b.return_date }));

    return computeAvailability(data.slug, (row as AvailabilityRow | null) ?? null, ranges, today);
  });

/** Admin: manual fully-booked switch per car. */
export const setCarAvailability = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        slug: z.string().min(1).max(160),
        fullyBooked: z.boolean(),
        blockedUntil: isoDate.nullable().optional(),
        note: z.string().trim().max(300).nullable().optional(),
      })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (!isAdmin) throw new Error("Forbidden");

    const { error } = await context.supabase.from("car_availability").upsert(
      {
        car_slug: data.slug,
        fully_booked: data.fullyBooked,
        blocked_until: data.fullyBooked ? (data.blockedUntil ?? null) : null,
        note: data.note ?? null,
        updated_by: context.userId,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "car_slug" },
    );
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/** Admin: recompute blocked-until dates from live reservations and sync every car row. */
export const syncAvailabilityFromReservations = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (!isAdmin) throw new Error("Forbidden");

    const today = isoToday();

    const [{ data: rows }, { data: bookings }] = await Promise.all([
      context.supabase.from("car_availability").select("car_slug, fully_booked, blocked_until, note"),
      context.supabase
        .from("bookings")
        .select("car_slug, pickup_date, return_date, stage, status")
        .gte("return_date", today),
    ]);

    const rangesBySlug = new Map<string, BookedRange[]>();
    for (const b of bookings ?? []) {
      if (b.stage === "cancelled" || b.stage === "no_show" || b.status === "cancelled") continue;
      const list = rangesBySlug.get(b.car_slug) ?? [];
      list.push({ pickup_date: b.pickup_date, return_date: b.return_date });
      rangesBySlug.set(b.car_slug, list);
    }

    const existing = new Map<string, AvailabilityRow>();
    for (const r of (rows ?? []) as AvailabilityRow[]) existing.set(r.car_slug, r);

    const slugs = new Set<string>([...existing.keys(), ...rangesBySlug.keys()]);
    const updates: {
      car_slug: string;
      fully_booked: boolean;
      blocked_until: string | null;
      note: string | null;
      updated_by: string;
      updated_at: string;
    }[] = [];

    let blocked = 0;
    let cleared = 0;

    for (const slug of slugs) {
      const row = existing.get(slug) ?? null;
      const freeFrom = reservationFreeDate(rangesBySlug.get(slug) ?? [], today);
      const isBlocked = freeFrom > today;
      const blockedUntil = isBlocked ? addDay(freeFrom, -1) : null;

      if (Boolean(row?.fully_booked) === isBlocked && (row?.blocked_until ?? null) === blockedUntil) {
        continue;
      }
      if (isBlocked) blocked += 1;
      else cleared += 1;

      updates.push({
        car_slug: slug,
        fully_booked: isBlocked,
        blocked_until: blockedUntil,
        note: row?.note ?? null,
        updated_by: context.userId,
        updated_at: new Date().toISOString(),
      });
    }

    if (updates.length) {
      const { error } = await context.supabase
        .from("car_availability")
        .upsert(updates, { onConflict: "car_slug" });
      if (error) throw new Error(error.message);
    }

    return { ok: true, updated: updates.length, blocked, cleared, checked: slugs.size };
  });
